import { useEffect, useMemo, useState } from "react";
import { api } from "../api";

/* Bedrock — fock.space canon, drawn as canon draws itself.
 *
 * Canon and the firm's graph are different epistemic objects (consult §4.5):
 * ours is a record of what we decided, canon is a record of what has been
 * RULED, by someone else, with an appeal window. So canon lives in its own
 * band, below the lanes, and never mixes into the firm's hairball.
 *
 * Three levels, always focused on something, back goes up one:
 *   Universe   — every body in a world at canon's OWN x/y (we run no layout;
 *                re-simulating it would make our picture and fock's disagree)
 *   Neighborhood — one concept: lenses, article, RATIFIED edges with the
 *                verified basis that argued each one in, latest ruling
 *   Dark matter — the published, attributed, UNTESTED claims anchored to that
 *                concept, from every homestead. Shown apart from the ratified
 *                edges on purpose: nobody has ruled on any of it.
 *
 * Alan, 2026-10-14: "i can't click into dark matter … it still doesn't
 * navigate into neighborhoods from the universe view". Neither existed here.
 */

type Universe = { id: string; slug: string; name: string; generation: number; bodies: number };
type Body = { id: string; slug: string | null; name: string | null; grain: string | null;
  mass: number | null; heat: number | null; x: number | null; y: number | null };
type Field = { configured: boolean; error?: string; universe?: string; generation?: number;
  bodies: Body[]; total_bodies?: number };
type Span = { type: string; text: string; target_slug?: string };
type Block = { type: string; text?: string; level?: number; spans?: Span[] };
type Concept = {
  id: string; slug: string; name: string; grain: string; mass?: number; heat?: number;
  lenses?: { plain?: string; tech?: string }; article?: Block[];
  source?: { kind?: string; url?: string; rev?: string; license?: string };
  dark_matter?: { count: number; note?: string };
  error?: string;
};
type Edge = {
  edge_id: string; type: string; direction: "in" | "out"; ratified_by_ruling_id?: string;
  basis?: string; body: Body & { open_by_design?: boolean };
};
type Ruling = { number?: string | number; disposition?: string; reasoning?: string;
  appealable_until?: string; issued_by_office?: string; cites_constitution?: string[] | string };
type Strata = { latest_ruling?: Ruling; entries?: unknown[]; error?: string };
type ConceptBundle = {
  configured: boolean; slug: string; universe: string;
  concept: Concept; strata: Strata;
  edges: { edges?: Edge[]; error?: string } | Edge[];
  measurements: { measurements?: unknown[]; error?: string } | unknown[];
  discrepancies: { discrepancies?: unknown[]; error?: string } | unknown[];
};
type DarkItem = { id: string; kind: string; label: string; note: string; url: string | null;
  handle: string | null; agent: boolean; created_at: string | null; relation: string };
type Dark = { configured: boolean; error?: string; count: number; items: DarkItem[] };
type SearchRes = {
  configured: boolean; error?: string;
  canon: { id: string; slug: string; name: string; universe: string; grain: string; rank: number }[];
  mine: { id: string; title: string; anchor_concept_id: string | null }[];
  dark: { id: string; label: string; note: string; url: string | null; anchor_universe: string;
    anchor_slug: string; handle: string; agent: boolean }[];
};

const GRAIN_R: Record<string, number> = { DOMAIN: 10, FIELD: 6.5, CONCEPT: 3.4, COMPONENT: 2 };
const GRAIN_ORDER = ["DOMAIN", "FIELD", "CONCEPT", "COMPONENT"];

export default function Bedrock() {
  const [universes, setUniverses] = useState<Universe[]>([]);
  const [universe, setUniverse] = useState("finance");
  const [field, setField] = useState<Field | null>(null);
  // The walk. trail[last] is the open concept; empty = universe view.
  const [trail, setTrail] = useState<string[]>([]);
  const [bundle, setBundle] = useState<ConceptBundle | null>(null);
  const [dark, setDark] = useState<Dark | null>(null);
  const [openDark, setOpenDark] = useState<string | null>(null);
  const [openBasis, setOpenBasis] = useState<string | null>(null);
  const [showRuling, setShowRuling] = useState(false);
  const [hoverBody, setHoverBody] = useState<string | null>(null);
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Search across the three buckets.
  const [q, setQ] = useState("");
  const [res, setRes] = useState<SearchRes | null>(null);
  const [bucket, setBucket] = useState<"canon" | "dark" | "mine">("canon");

  const slug = trail.length ? trail[trail.length - 1] : null;

  useEffect(() => {
    api.get<{ universes: Universe[] }>("/api/graph/canon/universes")
      .then((u) => setUniverses(u.universes ?? [])).catch(() => {});
  }, []);

  useEffect(() => {
    let live = true;
    setField(null);
    api.get<Field>(`/api/graph/canon/field?universe=${encodeURIComponent(universe)}`)
      .then((f) => { if (live) setField(f); })
      .catch((e) => { if (live) setError((e as Error).message); });
    return () => { live = false; };
  }, [universe]);

  useEffect(() => {
    if (!slug) { setBundle(null); setDark(null); return; }
    let live = true;
    setBundle(null); setDark(null); setOpenDark(null); setOpenBasis(null); setShowRuling(false);
    setLoading(slug);
    const u = encodeURIComponent(universe);
    api.get<ConceptBundle>(`/api/graph/canon/${encodeURIComponent(slug)}?universe=${u}`)
      .then((b) => { if (live) setBundle(b); })
      .catch((e) => { if (live) setError((e as Error).message); })
      .finally(() => { if (live) setLoading(null); });
    // Dark matter is its own fetch: it degrades independently and it is the
    // slow leg (37 items × a 2 KB note each on `inflation`).
    api.get<Dark>(`/api/graph/canon/${encodeURIComponent(slug)}/dark?universe=${u}`)
      .then((d) => { if (live) setDark(d); })
      .catch(() => { if (live) setDark({ configured: true, count: 0, items: [], error: "dark matter unavailable" }); });
    return () => { live = false; };
  }, [slug, universe]);

  useEffect(() => {
    if (q.trim().length < 2) { setRes(null); return; }
    const t = setTimeout(() => {
      api.get<SearchRes>(`/api/graph/canon/search?q=${encodeURIComponent(q.trim())}&universe=${encodeURIComponent(universe)}&limit=10`)
        .then(setRes).catch(() => setRes(null));
    }, 300);
    return () => clearTimeout(t);
  }, [q, universe]);

  function open(next: string, inUniverse?: string) {
    if (inUniverse && inUniverse !== universe) { setUniverse(inUniverse); setTrail([next]); return; }
    setTrail((t) => (t[t.length - 1] === next ? t : [...t, next]));
    setQ(""); setRes(null);
  }
  function back() { setTrail((t) => t.slice(0, -1)); }
  function home() { setTrail([]); }

  const edges: Edge[] = useMemo(() => {
    const e = bundle?.edges;
    if (!e) return [];
    return Array.isArray(e) ? e : (e.edges ?? []);
  }, [bundle]);
  const neighbourIds = useMemo(() => new Set(edges.map((e) => e.body?.id)), [edges]);
  const byId = useMemo(() => new Map((field?.bodies ?? []).map((b) => [b.id, b])), [field]);
  const current = useMemo(() => (field?.bodies ?? []).find((b) => b.slug === slug) ?? null, [field, slug]);

  // Edges grouped by the direction a reader thinks in: what this concept
  // rests on (out), and what rests on it (in).
  const grouped = useMemo(() => {
    const out = edges.filter((e) => e.direction === "out");
    const inn = edges.filter((e) => e.direction === "in");
    const byType = (list: Edge[]) => {
      const m = new Map<string, Edge[]>();
      for (const e of list) m.set(e.type, [...(m.get(e.type) ?? []), e]);
      return [...m.entries()].sort((a, b) => b[1].length - a[1].length);
    };
    return { out: byType(out), in: byType(inn) };
  }, [edges]);

  const concept = bundle?.concept && !bundle.concept.error ? bundle.concept : null;
  const ruling = bundle?.strata?.latest_ruling;
  const uni = universes.find((u) => u.slug === universe);

  return (
    <div className="card border-accent/30 space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <h2 className="label mb-0">Bedrock · fock.space canon</h2>
          {field?.generation != null && (
            <span className="badge bg-slate-700 text-slate-300">
              {uni?.name ?? universe} · gen {field.generation} · {field.total_bodies ?? field.bodies.length} bodies
            </span>
          )}
        </div>
        <div className="flex gap-1 flex-wrap">
          {universes.map((u) => (
            <button key={u.slug}
              className={`btn text-[11px] py-0.5 px-2 ${u.slug === universe ? "btn-primary" : ""}`}
              title={`${u.bodies} bodies`}
              onClick={() => { setUniverse(u.slug); setTrail([]); }}>
              {u.name}
            </button>
          ))}
        </div>
      </div>
      <p className="text-xs text-slate-500">
        Canon is what has been <b className="text-slate-400">ruled</b> — by someone else, with an appeal
        window — drawn at canon&rsquo;s own coordinates. Click a body to open its neighborhood; the
        dark matter under each concept is every published claim nobody has ruled on yet.
      </p>
      {error && <div className="text-down text-xs">{error}</div>}

      {/* Search — three buckets, one call */}
      <div className="relative">
        <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder={`Search ${uni?.name ?? universe} canon, our bodies, and dark matter…`}
          className="input w-full text-sm" />
        {q.trim().length >= 2 && (
          <div className="absolute z-20 mt-1 w-full card border-edge shadow-lg max-h-80 overflow-auto p-1 space-y-1">
            <div className="flex gap-1 px-1 pt-1">
              {(["canon", "dark", "mine"] as const).map((b) => (
                <button key={b} className={`btn text-[11px] py-0.5 px-2 ${bucket === b ? "btn-primary" : ""}`}
                  onClick={() => setBucket(b)}>
                  {b === "canon" ? "Canon" : b === "dark" ? "Dark matter" : "Ours"} {res ? `· ${res[b]?.length ?? 0}` : ""}
                </button>
              ))}
            </div>
            {!res && <div className="text-xs text-slate-500 p-2">searching…</div>}
            {res?.error && <div className="text-xs text-down p-2">{res.error}</div>}
            {res && bucket === "canon" && res.canon.map((h) => (
              <button key={h.id} className="block text-xs text-left w-full py-1 px-1.5 rounded hover:bg-slate-800"
                onClick={() => open(h.slug, h.universe)}>
                <span className="text-slate-200">{h.name}</span>
                <span className="text-slate-600 ml-2">{h.grain.toLowerCase()} · {h.universe}</span>
              </button>
            ))}
            {res && bucket === "dark" && res.dark.map((h) => (
              <button key={h.id} className="block text-xs text-left w-full py-1 px-1.5 rounded hover:bg-slate-800"
                onClick={() => { open(h.anchor_slug, h.anchor_universe); setOpenDark(h.id); }}>
                <span className="text-slate-200 line-clamp-1">{h.label}</span>
                <span className="text-slate-600">{h.handle}{h.agent ? " (agent)" : ""} · on {h.anchor_slug}</span>
              </button>
            ))}
            {res && bucket === "mine" && res.mine.map((h) => {
              const anchor = h.anchor_concept_id ? byId.get(h.anchor_concept_id) : undefined;
              return (
                <button key={h.id} className="block text-xs text-left w-full py-1 px-1.5 rounded hover:bg-slate-800"
                  disabled={!anchor?.slug} onClick={() => anchor?.slug && open(anchor.slug)}>
                  <span className="text-slate-200 line-clamp-1">{h.title}</span>
                  <span className="text-slate-600">{anchor?.name ? `anchored to ${anchor.name}` : "anchored to a component (not drawn at this zoom)"}</span>
                </button>
              );
            })}
            {res && res[bucket].length === 0 && <div className="text-xs text-slate-500 p-2">nothing in this bucket</div>}
          </div>
        )}
      </div>

      {/* Breadcrumb trail — back always goes up one */}
      <div className="flex items-center gap-1 text-xs flex-wrap">
        <button className={`btn text-[11px] py-0.5 px-2 ${!slug ? "btn-primary" : ""}`} onClick={home}>
          {uni?.name ?? universe}
        </button>
        {trail.map((s, i) => (
          <span key={`${s}-${i}`} className="flex items-center gap-1">
            <span className="text-slate-600">›</span>
            <button className={`btn text-[11px] py-0.5 px-2 ${i === trail.length - 1 ? "btn-primary" : ""}`}
              onClick={() => setTrail(trail.slice(0, i + 1))}>
              {byId.get(edges.find((e) => e.body.slug === s)?.body.id ?? "")?.name ?? s}
            </button>
          </span>
        ))}
        {slug && <button className="btn text-[11px] py-0.5 px-2 ml-auto" onClick={back}>← back</button>}
      </div>

      <div className={`grid gap-3 ${slug ? "lg:grid-cols-[1fr_1.4fr]" : ""}`}>
        {/* The field, at canon's coordinates */}
        <FieldMap bodies={field?.bodies ?? []} loading={!field} currentId={current?.id ?? null}
          neighbourIds={neighbourIds} hover={hoverBody} onHover={setHoverBody}
          onPick={(b) => b.slug && open(b.slug)} compact={!!slug} />

        {/* Neighborhood */}
        {slug && (
          <div className="space-y-3 min-w-0">
            {loading === slug && !bundle && <div className="text-xs text-slate-500">reading canon…</div>}
            {bundle && !concept && (
              <div className="text-xs text-down">{bundle.concept?.error ?? "concept unavailable"}</div>
            )}
            {concept && (
              <>
                <div>
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="font-bold text-slate-100 text-base">{concept.name}</span>
                    <span className="text-[11px] text-slate-500 uppercase tracking-wide">{concept.grain}</span>
                    {concept.mass != null && <span className="text-[11px] text-slate-600">mass {Math.round(concept.mass)}</span>}
                  </div>
                  {concept.lenses?.plain && <p className="text-sm text-slate-300 mt-1">{concept.lenses.plain}</p>}
                  {concept.lenses?.tech && <p className="text-xs text-slate-500 mt-1">{concept.lenses.tech}</p>}
                </div>

                {/* Ratified edges, with the basis that argued each one in */}
                <div className="grid sm:grid-cols-2 gap-3">
                  <EdgeColumn title="What it rests on" groups={grouped.out} openBasis={openBasis}
                    setOpenBasis={setOpenBasis} onOpen={(s) => open(s)} />
                  <EdgeColumn title="What rests on it" groups={grouped.in} openBasis={openBasis}
                    setOpenBasis={setOpenBasis} onOpen={(s) => open(s)} />
                </div>
                {edges.length === 0 && <div className="text-xs text-slate-600">no ratified edges around this concept</div>}

                {/* The ruling that governs it */}
                {ruling && (
                  <div className="border-t border-edge pt-2 text-xs">
                    <button className="text-slate-400 hover:text-slate-200" onClick={() => setShowRuling(!showRuling)}>
                      {showRuling ? "▾" : "▸"} Ruling {ruling.number ?? "—"} · {ruling.disposition ?? "—"}
                      {ruling.issued_by_office ? ` · ${ruling.issued_by_office}` : ""}
                      {ruling.appealable_until ? ` · appealable until ${new Date(ruling.appealable_until).toLocaleDateString()}` : ""}
                    </button>
                    {showRuling && ruling.reasoning && (
                      <p className="text-slate-400 mt-1 whitespace-pre-wrap">{ruling.reasoning}</p>
                    )}
                  </div>
                )}

                {/* The article — its "dig" links navigate */}
                {concept.article && concept.article.length > 0 && (
                  <details className="text-xs border-t border-edge pt-2">
                    <summary className="text-slate-400 cursor-pointer">Article</summary>
                    <div className="space-y-2 mt-2 text-slate-300 leading-relaxed">
                      {concept.article.map((b, i) => b.type === "heading"
                        ? <div key={i} className="font-semibold text-slate-200 mt-2">{b.text}</div>
                        : <p key={i}>{(b.spans ?? []).map((s, j) => s.type === "dig" && s.target_slug
                            ? <button key={j} className="text-gold hover:underline" onClick={() => open(s.target_slug!)}>{s.text}</button>
                            : <span key={j}>{s.text}</span>)}</p>)}
                      {concept.source && (
                        <div className="text-slate-600">source: {concept.source.kind}{concept.source.rev ? ` · ${concept.source.rev}` : ""}{concept.source.license ? ` · ${concept.source.license}` : ""}</div>
                      )}
                    </div>
                  </details>
                )}
              </>
            )}

            {/* Dark matter — apart, on purpose */}
            <div className="border-t border-edge pt-2 space-y-1">
              <div className="flex items-baseline justify-between gap-2">
                <h3 className="label mb-0">Dark matter{dark ? ` · ${dark.count}` : ""}</h3>
                <span className="text-[11px] text-slate-600">published · attributed · untested by any ruling</span>
              </div>
              {!dark && <div className="text-xs text-slate-500">reading…</div>}
              {dark?.error && <div className="text-xs text-down">{dark.error}</div>}
              {dark && dark.count === 0 && !dark.error && (
                <div className="text-xs text-slate-600">nobody has filed a claim here yet</div>
              )}
              {dark?.items.map((it) => {
                const isOpen = openDark === it.id;
                return (
                  <div key={it.id} className={`rounded px-2 py-1 ${isOpen ? "bg-slate-800/60" : "hover:bg-slate-800/40"}`}>
                    <button className="w-full text-left text-xs" onClick={() => setOpenDark(isOpen ? null : it.id)}>
                      <span className={`text-slate-200 ${isOpen ? "" : "line-clamp-1"}`}>{it.label}</span>
                      <span className="block text-[11px] text-slate-500">
                        <span className={it.agent ? "text-gold/80" : "text-slate-400"}>{it.handle ?? "anonymous"}</span>
                        {it.agent ? " · agent" : ""} · {it.relation.replace("_", " ")}
                        {it.created_at ? ` · ${new Date(it.created_at).toLocaleDateString()}` : ""}
                      </span>
                    </button>
                    {isOpen && (
                      <div className="text-xs text-slate-300 mt-1 space-y-1">
                        {it.note && <p className="whitespace-pre-wrap leading-relaxed max-h-72 overflow-auto pr-1">{it.note}</p>}
                        {it.url && (
                          <a href={it.url} target="_blank" rel="noreferrer" className="text-gold hover:underline break-all">
                            {it.url} ↗
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function EdgeColumn({ title, groups, openBasis, setOpenBasis, onOpen }: {
  title: string; groups: [string, Edge[]][]; openBasis: string | null;
  setOpenBasis: (id: string | null) => void; onOpen: (slug: string) => void;
}) {
  return (
    <div>
      <h3 className="label">{title}</h3>
      {groups.length === 0 && <div className="text-xs text-slate-600">—</div>}
      {groups.map(([type, list]) => (
        <div key={type} className="mb-1.5">
          <div className="text-[11px] text-slate-500 font-mono">{type} · {list.length}</div>
          {list.map((e) => {
            const isOpen = openBasis === e.edge_id;
            return (
              <div key={e.edge_id} className="text-xs">
                <div className="flex items-center gap-1">
                  <button className="text-slate-200 hover:text-gold text-left truncate"
                    disabled={!e.body?.slug} onClick={() => e.body?.slug && onOpen(e.body.slug)}>
                    {e.body?.name ?? e.body?.slug ?? "?"}
                  </button>
                  <span className="text-slate-600 text-[10px] uppercase shrink-0">{e.body?.grain?.slice(0, 4)}</span>
                  {e.basis && (
                    <button className="text-slate-600 hover:text-slate-300 ml-auto shrink-0 text-[11px]"
                      title="the evidence that argued this edge into canon"
                      onClick={() => setOpenBasis(isOpen ? null : e.edge_id)}>
                      {isOpen ? "basis ▾" : "basis ▸"}
                    </button>
                  )}
                </div>
                {isOpen && e.basis && (
                  <p className="text-slate-400 pl-2 border-l border-edge my-1 whitespace-pre-wrap">{e.basis}</p>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

/* The field: every DOMAIN/FIELD/CONCEPT body of a universe at canon's own
 * x/y. Size by grain, tint by heat (a ready-made recency channel). COMPONENTs
 * are left out at this zoom — they are 78% of the bodies and the detail
 * nobody can read in an overview; they arrive as edge bodies once a concept
 * is open. */
function FieldMap({ bodies, loading, currentId, neighbourIds, hover, onHover, onPick, compact }: {
  bodies: Body[]; loading: boolean; currentId: string | null; neighbourIds: Set<string>;
  hover: string | null; onHover: (id: string | null) => void; onPick: (b: Body) => void; compact: boolean;
}) {
  const drawn = useMemo(() => bodies.filter((b) => b.x != null && b.y != null)
    .sort((a, b) => GRAIN_ORDER.indexOf(a.grain ?? "") - GRAIN_ORDER.indexOf(b.grain ?? "")), [bodies]);
  const box = useMemo(() => {
    if (drawn.length === 0) return { x: 0, y: 0, w: 100, h: 100 };
    const xs = drawn.map((b) => b.x as number), ys = drawn.map((b) => b.y as number);
    const minX = Math.min(...xs), maxX = Math.max(...xs), minY = Math.min(...ys), maxY = Math.max(...ys);
    const pad = Math.max(maxX - minX, maxY - minY) * 0.06;
    return { x: minX - pad, y: minY - pad, w: (maxX - minX) + pad * 2, h: (maxY - minY) + pad * 2 };
  }, [drawn]);
  const maxHeat = useMemo(() => Math.max(1e-9, ...drawn.map((b) => b.heat ?? 0)), [drawn]);
  // Radii in viewBox units so they scale with the field, not the pixel grid.
  const unit = box.w / 1000;
  const focus = currentId != null;
  const hovered = drawn.find((b) => b.id === hover);

  return (
    <div className="relative min-w-0">
      {loading && <div className="text-xs text-slate-500 p-4 text-center">reading canon&rsquo;s field…</div>}
      {!loading && drawn.length === 0 && <div className="text-xs text-slate-600 p-4 text-center">field unavailable</div>}
      {drawn.length > 0 && (
        <svg viewBox={`${box.x} ${box.y} ${box.w} ${box.h}`} className={`w-full ${compact ? "max-h-[360px]" : "max-h-[520px]"}`}
          style={{ aspectRatio: `${box.w} / ${box.h}` }} onMouseLeave={() => onHover(null)}>
          {drawn.map((b) => {
            const r = (GRAIN_R[b.grain ?? "CONCEPT"] ?? 3) * unit * 1.6;
            const heat = Math.min(1, (b.heat ?? 0) / maxHeat);
            const isCur = b.id === currentId, isNb = neighbourIds.has(b.id), isHov = b.id === hover;
            const dim = focus && !isCur && !isNb;
            const label = b.grain === "DOMAIN" || b.grain === "FIELD" || isCur || isNb || isHov;
            return (
              <g key={b.id} className="cursor-pointer" opacity={dim ? 0.28 : 1}
                onMouseEnter={() => onHover(b.id)} onClick={() => onPick(b)}>
                <circle cx={b.x as number} cy={b.y as number} r={isCur ? r * 1.5 : r}
                  fill={isCur ? "#f5c451" : isNb ? "#e2b84a" : `rgba(226,184,74,${0.25 + heat * 0.65})`}
                  stroke={isCur || isNb || isHov ? "#f5c451" : "rgba(148,163,184,0.35)"} strokeWidth={unit * (isCur ? 3 : 1.2)} />
                {label && (
                  <text x={(b.x as number) + r + unit * 4} y={(b.y as number) + unit * 4}
                    fontSize={unit * (b.grain === "DOMAIN" ? 24 : b.grain === "FIELD" ? 17 : 14)}
                    fontWeight={b.grain === "DOMAIN" ? 700 : b.grain === "FIELD" ? 600 : 400}
                    fill={isCur ? "#f5c451" : dim ? "rgba(148,163,184,0.6)" : "#cbd5e1"}
                    style={{ pointerEvents: "none" }}>
                    {b.name}
                  </text>
                )}
                <title>{b.name} · {b.grain?.toLowerCase()}{b.mass != null ? ` · mass ${Math.round(b.mass)}` : ""}</title>
              </g>
            );
          })}
        </svg>
      )}
      {hovered && (
        <div className="absolute left-2 bottom-2 text-[11px] text-slate-300 bg-slate-900/80 rounded px-2 py-1 pointer-events-none">
          {hovered.name} <span className="text-slate-500">· {hovered.grain?.toLowerCase()} · click to open</span>
        </div>
      )}
    </div>
  );
}
